/* global adobe */
import mergeDeep from './mergeDeep';

/**
 * applyOffers
 * @description Applies offers to the given component data by deep merging them.
 * @param {Object[]} offers - An array of offers to be applied.
 * @param {Object} componentData - The original component data to be updated.
 * @param {Function} cb - A callback function that receives the updated component data.
 * @returns {void}
 */
const applyOffers = (offers, componentData, cb) => {
  const updatedComponentData = componentData;

  if (offers.length) {
    offers.forEach((offer) => {
      const {
        componentName,
        modelVariation,
        variant,
        update = null,
        // LP-4222 Sale Event test
        sortedActivities,
        // LP-4422 Purchase propensity test
        propensity,
      } = offer;
      const componentExists = !!componentData[componentName];
      const modelVariationExists = !!componentData[modelVariation];
      const variantExists = !!variant;
      // LP-4222 Sale Event test
      const sortedActivitiesValue = sortedActivities || '';
      // LP-4422 Purchase propensity test
      const propensityValue = propensity || '';

      // make sure both the component and model variant exist in componentModels
      if (componentExists && modelVariationExists && variantExists) {
        if (update) {
          const merged = mergeDeep(
            componentData[componentName],
            componentData[modelVariation],
            {
              variant,
              sortedActivities: sortedActivitiesValue, // LP-4222 Sale Event test
              propensity: propensityValue, // LP-4422 Purchase propensity test
            },
          );
          updatedComponentData[componentName] = merged;
        } else {
          updatedComponentData[componentName] = mergeDeep(
            componentData[modelVariation],
            {
              variant,
              sortedActivities: sortedActivitiesValue, // LP-4222 Sale Event test
              propensity: propensityValue, // LP-4422 Purchase propensity test
            }
          );
        }
      } else if (componentExists && !!variantExists) {
        updatedComponentData[componentName] = mergeDeep(componentData[componentName], {
          variant,
          sortedActivities: sortedActivitiesValue, // LP-4222 Sale Event test
          propensity: propensityValue, // LP-4422 Purchase propensity test
        });
      }
    });
  }
  cb(updatedComponentData);
};

/**
 * getAndApplyAdobeTargetOffers
 * @description Retrieves a JSON Offer by calling adobe.target.getOffer.
 *  The retrieved offer will be deep merged into the provided componentModel,
 *  and the updated componentModel will be passed back through the onSuccess callback.
 * @param {Object} componentData is a copy of the componentModel
 * @param {Function} onSuccess - Callback for success, receives the updated componentModel.
 * @param {Function} onError - Callback for error.
 * @param {Object} [options] - Options for the Adobe Target request
 * (default mbox: 'target-global-mbox').
 * @returns {void}
 */
const getAndApplyAdobeTargetOffers = (componentData, onSuccess, onError, options = { mbox: 'target-global-mbox' }) => {
  if (typeof adobe !== 'undefined' && adobe.target) {
    return adobe.target.getOffer({
      ...options,
      success: (data) => {
        const filteredOffers = data.filter((offer) => offer?.action === 'setJson');
        if (filteredOffers.length) {
          filteredOffers.forEach((offer) => {
            const { content } = offer;
            content.forEach((individualOffer) => {
              applyOffers(individualOffer, componentData, onSuccess);
            });
          });
        } else {
          onSuccess();
        }
      },
      error: () => {
        onError();
      },
    });
  }
  return onError();
};

/* c8 ignore start */
/**
 * Generates a payload structure based on the presence of specific mbox parameters,
 * Consider "pageLoad" to fetch selectors not assigned to any view (i.e., 'target-global-mbox').
 *
 * @param {Object} adobeTargetEWData - Adobe Target data structure.
 * @returns {Array|null} - Payload structure based on the provided Adobe Target data.
 */
const handleDynamicAdobeTargetResponse = (adobeTargetEWData) => {
  if (!adobeTargetEWData || !adobeTargetEWData.execute) {
    // eslint-disable-next-line
    console.error('Invalid or missing Adobe Target data structure:', adobeTargetEWData);
    return null;
  }

  const { execute: { pageLoad, mboxes } } = adobeTargetEWData;

  // payload structure when passing 'pageLoad'
  if (pageLoad?.options?.length) return pageLoad.options;
  // payload structure when passing specific 'mboxes'
  if (mboxes?.length) return mboxes;

  // eslint-disable-next-line
  console.info('Unsupported structure in adobeTargetEWData:', adobeTargetEWData);
  return null;
};

const getAndParseJsonFromScriptById = (scriptId) => {
  const script = document.getElementById(scriptId);
  const hasContent = () => (script !== null && script.innerHTML !== '');
  if (hasContent()) {
    try {
      const jsonData = JSON.parse(script.innerHTML);
      return jsonData;
    } catch (error) {
      // eslint-disable-next-line
      console.error(`Error parsing JSON from script with ID ${scriptId}:`, error);
      return false;
    }
  }
  // eslint-disable-next-line
  console.info(`Script element with ID '${scriptId}' not found.`);
  return false;
};

/**
 * Applies EdgeWorker Adobe Target content from the injected script if available.
 * Otherwise, fetches Adobe Target content on the client side.
 *
 * @param {Object} componentData is a copy of the componentModel
 * @param {Function} onSuccess - Callback for success, receives the updated componentModel.
 * @param {Function} onError - Callback for error.
 * @returns {boolean} - Returns false if Adobe Target content retrieval and application fail.
 */
const getAndApplyEWContent = (
  componentData,
  onSuccess,
  onError,
) => {
  const EW_AT_SCRIPT_ID = 'ew-adobe-target-data';
  const ewAdobeTargetData = getAndParseJsonFromScriptById(EW_AT_SCRIPT_ID);

  if (ewAdobeTargetData) {
    const adobeTargetOffers = handleDynamicAdobeTargetResponse(ewAdobeTargetData);

    if (adobeTargetOffers.length) {
      adobeTargetOffers
        .filter((offer) => offer.type === 'json')
        .forEach((jsonOffer) => {
          applyOffers(jsonOffer.content, componentData, onSuccess);
        });

      return;
    }
    onSuccess(componentData);
  } else {
    onError();
  }
};
/* c8 ignore end */

export { getAndApplyAdobeTargetOffers, applyOffers, getAndApplyEWContent };
